/**
 * iOS Push Notifications (Capacitor + APNs).
 *
 * Design goals for this file (v2 — no more silent hangs):
 *
 *   1. EVERY native Capacitor call is wrapped in a 5s timeout via
 *      `withTimeout()`. If iOS is in a weird state where the plugin
 *      simply never responds, the UI must never be stuck on "checking…".
 *
 *   2. The permission + register + token-received + backend-save chain
 *      is treated as a single atomic operation. `initIOSPushNotifications`
 *      only resolves `{ ok: true }` once the token has been received AND
 *      the backend has successfully persisted it. Any earlier failure
 *      short-circuits with a labelled `stage` string.
 *
 *   3. Every stage that can fail (check / request / register / token-save)
 *      reports itself through `onStep` with a `stage` marker so the UI
 *      diagnostics panel can render EXACTLY which phase timed out or
 *      threw.
 *
 * `onStep` shape (all steps are optional; caller can render whichever it
 * wants):
 *   { step: 'native-check',        ok: bool }
 *   { step: 'check-permissions',   permission: 'granted'|'denied'|'prompt'|... }
 *   { step: 'request-permissions', permission: '...' }
 *   { step: 'register-called' }
 *   { step: 'token-received',      tokenLast8: '...' }
 *   { step: 'token-saved',         ok: bool, error?: string, tokenLast8?: string }
 *   { step: 'registration-error',  error: string, stage: 'check'|'request'|'register'|'token-save'|'listener' }
 *   { step: 'denied',              message: string }
 *   { step: 'done' }
 */
import axios from 'axios';
import { toast } from 'sonner';
import { isIOS, isNative } from './platform';
import { Capacitor } from '@capacitor/core';
// STATIC top-level import (previously a dynamic `import()` inside
// `loadPlugin()` — see below). Capacitor's push-notifications plugin
// is safe to import on any platform: on web its methods are no-ops
// that throw only when called, so evaluating this line at module
// load time cannot hang. Making the reference static means the button
// flow never has to `await` a dynamic-import promise (which was the
// exact hang observed in Build 47 / bundle 79046625 — the flow
// reached `native-check` and then blocked forever on the dynamic
// `import('@capacitor/push-notifications')`).
import { PushNotifications } from '@capacitor/push-notifications';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// 5-second timeout used to guard the on-demand `readIOSPushPermission`
// diagnostic. Registration itself uses per-phase timeouts declared below.
const NATIVE_CALL_TIMEOUT_MS = 5000;

let _listenersAttached = false;
let _lastTokenValue = null;

// The `onStep` callback for the CURRENT registration attempt. Updated
// on every call to `initIOSPushNotifications` so that a listener
// attached during a previous page render (e.g. a stale ProfilePage
// instance) never routes step events to an out-of-date React closure.
// Listener callbacks read this reference at fire-time, not at
// attachment time, which is what makes callback swap-out possible
// without re-attaching listeners.
let _activeOnStep = null;

// Registration-in-flight coordination. When `initIOSPushNotifications`
// runs, it hands the resolver of this promise to the `registration`
// listener so the outer flow can await the full token→backend-save chain
// atomically.
let _pendingRegistration = null;

// Small helper — always route through the live callback so the caller
// that kicked off THIS registration attempt is the one that renders
// the diagnostics, even if the module-level listeners were installed
// during a previous attempt.
function emit(evt) {
  const cb = _activeOnStep;
  if (typeof cb === 'function') {
    try { cb(evt); } catch (_) { /* never let a UI callback break the flow */ }
  }
}

/**
 * Race a promise against a timeout. Rejects with a labelled Error if the
 * timeout fires first, so callers can attribute which native call hung.
 * Timeout-triggered errors carry `.isTimeout = true` so callers can
 * distinguish a stall (`request-timeout`) from a real native rejection
 * (`request-error`).
 */
function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const err = new Error(`${label} timed out after ${ms}ms`);
      err.isTimeout = true;
      reject(err);
    }, ms);
    Promise.resolve(promise).then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

/**
 * Attach the four Capacitor listeners we care about. Attached exactly
 * once per process; safe to call multiple times.
 *
 * IMPORTANT: `PushNotifications.addListener` is asynchronous — it
 * returns a Promise that resolves with a `PluginListenerHandle` once
 * the native side has actually subscribed. If we do NOT await these,
 * a fast `register()` call can fire the `registration` event on the
 * native bridge BEFORE the JS-side handler is wired up, and the token
 * is silently lost. All four attachments are therefore awaited, and
 * `_listenersAttached` is flipped to `true` only after every one of
 * them settles successfully. If any single attachment throws, the
 * flag is rolled back, `stage: 'listener'` is reported and the caller
 * must abort before calling `register()`.
 *
 * The `registration` listener does the heavy lifting: it uploads the
 * token to the backend with retries, then resolves any in-flight
 * `_pendingRegistration` so the caller of `initIOSPushNotifications`
 * knows the full chain has settled.
 *
 * Returns `{ ok: true }` on success or `{ ok: false, error }` on
 * failure so the caller can surface the exact reason.
 */
/**
 * Register the four Capacitor listeners in a NON-BLOCKING loose pattern.
 *
 * ⚠️  This function must NEVER be `await`-ed by the outer permission
 * flow. Doing so has been observed on physical devices (Build 47,
 * bundle 46f9631e) to hang for 13+ minutes before
 * `requestPermissions()` was ever reached — because the native
 * `PushNotifications.addListener()` bridge call itself never resolved,
 * blocking every downstream step including the request-timeout
 * watchdog. Instead, callers fire this off and immediately proceed
 * to `requestPermissions()`.
 *
 * Each of the four `addListener()` calls has its own 2s per-call
 * timeout (`LISTENER_ATTACH_TIMEOUT_MS`). A hang or throw on any one
 * listener:
 *   - Emits `listener-setup-timeout` (or `listener-setup-error`) with
 *     the listener name.
 *   - Does NOT abort the flow. Registration proceeds anyway.
 *
 * Capacitor's plugin bridge retains events fired before their
 * listeners are subscribed (`retainedEventArguments`), so even if a
 * listener attaches AFTER `register()` fires the token, the event
 * is buffered and delivered as soon as the listener is ready.
 *
 * Returns a Promise that resolves (never rejects) with an array of
 * `{ name, ok, error? }` results, plus emits `listeners-ready` (all
 * ok) or `listener-setup-error` (any failure) at the end. The
 * outer flow is free to ignore the return value entirely — the
 * emitted stages are the source of truth for the UI.
 */
async function attachListenersLoose(Push) {
  emit({ step: 'attaching-listeners' });

  const definitions = [
    ['registration', async (token) => {
      const value = token && token.value;
      if (!value) {
        emit({ step: 'registration-error', error: 'registration event fired with empty token', stage: 'listener' });
        if (_pendingRegistration) {
          _pendingRegistration.resolve({ ok: false, stage: 'listener', error: 'empty-token' });
          _pendingRegistration = null;
        }
        return;
      }
      _lastTokenValue = value;
      const last8 = value.slice(-8);
      emit({ step: 'token-received', tokenLast8: last8 });

      // Best-effort device label so admin tester can identify the phone.
      let deviceLabel = 'iOS device';
      try {
        const ua = navigator.userAgentData;
        if (ua && Array.isArray(ua.brands)) {
          deviceLabel = `iOS ${ua.mobile ? 'phone' : 'tablet'}`;
        }
      } catch (_) { /* ignore */ }

      emit({ step: 'saving-token', tokenLast8: last8 });
      // Retry the token upload up to 3× with exponential backoff. A
      // network blip during app cold-start must not leave the device
      // silently un-addressable until next launch.
      let saved = false;
      let lastErr = null;
      for (let attempt = 1; attempt <= 3 && !saved; attempt++) {
        try {
          await axios.post(
            `${API}/push/ios-token`,
            { token: value, platform: 'ios', device_label: deviceLabel },
            { withCredentials: true, timeout: 8000 },
          );
          saved = true;
        } catch (err) {
          lastErr = err?.response?.data?.detail || err?.message || String(err);
          await new Promise((r) => setTimeout(r, 400 * attempt * attempt));
        }
      }
      emit({
        step: saved ? 'success' : 'token-save-error',
        ok: saved,
        error: saved ? null : lastErr,
        tokenLast8: last8,
      });

      // Settle any in-flight init call so the toggle can move to
      // its final state.
      if (_pendingRegistration) {
        if (saved) {
          _pendingRegistration.resolve({ ok: true, tokenLast8: last8 });
        } else {
          _pendingRegistration.resolve({ ok: false, stage: 'token-save', error: lastErr, tokenLast8: last8 });
        }
        _pendingRegistration = null;
      }
    }],
    ['registrationError', (err) => {
      const msg = (err && (err.error || err.message)) || 'unknown';
      emit({ step: 'registration-error', error: String(msg), stage: 'register' });
      if (_pendingRegistration) {
        _pendingRegistration.resolve({ ok: false, stage: 'register', error: String(msg) });
        _pendingRegistration = null;
      }
    }],
    ['pushNotificationReceived', (notification) => {
      const title = notification?.title || 'New activity in your family';
      const body = notification?.body || '';
      toast(title, { description: body });
    }],
    ['pushNotificationActionPerformed', (action) => {
      const deeplink = action?.notification?.data?.deeplink;
      if (deeplink && typeof deeplink === 'string' && deeplink.startsWith('/')) {
        window.history.pushState({}, '', deeplink);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }],
  ];

  const results = await Promise.all(definitions.map(async ([name, handler]) => {
    try {
      await rejectAfter(
        Push.addListener(name, handler),
        LISTENER_ATTACH_TIMEOUT_MS,
        `addListener(${name})`,
      );
      emit({ step: `listener-attached-${name}` });
      return { name, ok: true };
    } catch (err) {
      const msg = err?.message || String(err);
      // 2s hard cap per listener — a hang here MUST NOT block the flow.
      emit({ step: 'listener-setup-timeout', name, error: msg });
      return { name, ok: false, error: msg };
    }
  }));

  const allOk = results.every((r) => r.ok);
  _listenersAttached = allOk;
  emit({ step: allOk ? 'listeners-ready' : 'listener-setup-error', results });
  return results;
}

// Concurrency guard for `initIOSPushNotifications`. If a second caller
// arrives while a registration attempt is still running, they get the
// SAME in-flight promise back rather than triggering a second parallel
// permission/register flow. Cleared in a `finally` on the wrapper so
// the guard is released regardless of success, denial, thrown error,
// or timeout.
let _initializationPromise = null;

/**
 * Full registration flow — DIRECT PATH per iOS acceptance-test brief.
 *
 * Deliberately does NOT call `PushNotifications.checkPermissions()`
 * first: on some iPhones that call has been observed to hang
 * indefinitely, blocking the whole flow before the OS prompt can
 * appear. Capacitor's `requestPermissions()` documents that it
 * returns the existing permission state if the user has already
 * made a choice, so a separate check is not needed — one call
 * covers both the "prompt now" and "already decided" cases.
 *
 * Each native call has its OWN 8-second timeout with a labelled
 * stage so a stall is attributable to a specific phase (not hidden
 * behind a generic overall timeout).
 *
 * Return shape (exactly one of the following stages):
 *   { ok: true,  stage: 'success',       tokenLast8, permission: 'granted' }
 *   { ok: false, stage: 'native-check',  error: 'not-native' }
 *   { ok: false, stage: 'plugin',        error }
 *   { ok: false, stage: 'listener',      error }
 *   { ok: false, stage: 'request-timeout', error }
 *   { ok: false, stage: 'request-denied',  permission }
 *   { ok: false, stage: 'register-timeout',error, permission }
 *   { ok: false, stage: 'registration-error', error, permission }   // registrationError listener fired
 *   { ok: false, stage: 'token-timeout',   error, permission }
 *   { ok: false, stage: 'token-save-error',error, tokenLast8, permission }
 */
export function initIOSPushNotifications(onStep) {
  if (_initializationPromise) {
    return _initializationPromise;
  }
  _initializationPromise = _runInit(onStep).finally(() => {
    _initializationPromise = null;
  });
  return _initializationPromise;
}

// Per-call timeouts per the direct-flow spec.
const REQUEST_PERMISSIONS_TIMEOUT_MS = 8000;
const REGISTER_TIMEOUT_MS             = 8000;
const TOKEN_WAIT_TIMEOUT_MS           = 10000;
// 2s hard cap for each individual `PushNotifications.addListener()`
// call — a hang here MUST NOT block the outer request-permission flow.
const LISTENER_ATTACH_TIMEOUT_MS      = 2000;
// 12s top-level UI safety net. Kicks in ONLY if every individual
// stage timeout has somehow failed to fire (e.g. the native bridge
// is completely wedged). Never replaces the individual stage timeouts.
const FLOW_WATCHDOG_TIMEOUT_MS        = 12000;

// Reject-on-timeout helper — used only inside `_runInit` because that
// function catches rejections explicitly per stage. Timeout-triggered
// errors carry `.isTimeout = true` so callers can distinguish a stall
// (`request-timeout` / `register-timeout`) from a genuine native
// rejection (`request-error`).
function rejectAfter(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      const err = new Error(`${label} timed out after ${ms}ms`);
      err.isTimeout = true;
      reject(err);
    }, ms);
    Promise.resolve(promise).then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

async function _runInit(onStep) {
  _activeOnStep = typeof onStep === 'function' ? onStep : null;

  // Track the last completed operation so the watchdog can include it
  // in the failure payload. Updated at every stage transition.
  let lastCompletedOp = 'init';

  // ---- 12s top-level watchdog ----
  // Rejects with a labelled Error if the entire flow hasn't settled
  // in 12s. Used as one arm of a Promise.race against the real work
  // below so a totally wedged native bridge can't leave the UI stuck
  // on "checking platform…" forever.
  let watchdogTimer = null;
  const watchdogPromise = new Promise((_, reject) => {
    watchdogTimer = setTimeout(() => {
      emit({ step: 'flow-watchdog-timeout', lastCompletedOp });
      reject(new Error(`flow-watchdog-timeout after ${FLOW_WATCHDOG_TIMEOUT_MS}ms (last: ${lastCompletedOp})`));
    }, FLOW_WATCHDOG_TIMEOUT_MS);
  });

  const inner = (async () => {
    emit({ step: 'flow-started' });
    lastCompletedOp = 'flow-started';

    // ---- Native platform gate (SYNCHRONOUS) ----
    // Uses Capacitor's own synchronous predicates directly — no async
    // work between `flow-started` and `native-check-complete`, so the
    // whole segment executes inside the same microtask. Requirement:
    // flow-started → requesting-permission in <100ms.
    const isNativeIOS =
      Capacitor.isNativePlatform() &&
      Capacitor.getPlatform() === 'ios';
    if (!isNativeIOS) {
      emit({ step: 'request-error', error: 'not-native', stage: 'native-check' });
      emit({ step: 'registration-error', error: 'not-native', stage: 'native-check' });
      return { ok: false, stage: 'native-check', permission: null, error: 'not-native' };
    }
    emit({ step: 'native-check-complete', platform: 'ios', native: true });
    lastCompletedOp = 'native-check-complete';

    // ---- Kick off listener attachment (FIRE-AND-FORGET) ----
    // Non-blocking. Each addListener has its own 2s cap; a hang emits
    // `listener-setup-timeout` for that listener but never blocks the
    // outer permission flow. Capacitor buffers events fired before
    // subscription, so a token arriving before the listener attaches
    // is delivered as soon as the listener is ready.
    const listenerSetupPromise = attachListenersLoose(PushNotifications);
    listenerSetupPromise.catch(() => { /* impossible; safety net */ });

    // ---- Plugin is already imported statically → ready immediately ----
    // No dynamic-import await; this line is the last checkpoint before
    // the actual native call. Emitting here proves the flow reached
    // the plugin call site (which was the specific gap in bundle
    // 79046625: watchdog fired at `last: native-check` because there
    // was no marker between native-check and the awaited dynamic
    // import).
    emit({ step: 'plugin-ready' });
    lastCompletedOp = 'plugin-ready';

    // ---- requestPermissions() — 8s timeout ----
    let perm;
    try {
      emit({ step: 'requesting-permission' });
      lastCompletedOp = 'requesting-permission';
      perm = await rejectAfter(
        PushNotifications.requestPermissions(),
        REQUEST_PERMISSIONS_TIMEOUT_MS,
        'requestPermissions',
      );
      lastCompletedOp = `permission-${perm?.receive || 'unknown'}`;
    } catch (err) {
      const msg = err?.message || String(err);
      if (err?.isTimeout) {
        emit({ step: 'request-timeout', error: msg });
        emit({ step: 'registration-error', error: msg, stage: 'request-timeout' });
        return { ok: false, stage: 'request-timeout', permission: null, error: msg };
      }
      // Genuine native rejection (permission API threw, plugin bridge
      // error, etc.) — surfaced as a distinct stage per spec.
      emit({ step: 'request-error', error: msg });
      emit({ step: 'registration-error', error: msg, stage: 'request-error' });
      return { ok: false, stage: 'request-error', permission: null, error: msg };
    }

    if (perm.receive !== 'granted') {
      emit({ step: 'request-denied', permission: perm.receive });
      return { ok: false, stage: 'request-denied', permission: perm.receive };
    }
    emit({ step: 'request-granted', permission: perm.receive });
    lastCompletedOp = 'request-granted';

    // ---- register() — 8s timeout ----
    // Set up the pending-registration promise BEFORE calling register()
    // so the `registration` listener has a resolver to hand off to when
    // the token arrives + is saved to the backend.
    const settlePromise = new Promise((resolve) => {
      _pendingRegistration = { resolve };
    });

    try {
      emit({ step: 'calling-register' });
      lastCompletedOp = 'calling-register';
      await rejectAfter(PushNotifications.register(), REGISTER_TIMEOUT_MS, 'register');
      lastCompletedOp = 'register-called';
    } catch (err) {
      const msg = err?.message || String(err);
      emit({ step: 'register-timeout', error: msg });
      emit({ step: 'registration-error', error: msg, stage: 'register-timeout' });
      _pendingRegistration = null;
      return { ok: false, stage: 'register-timeout', permission: perm.receive, error: msg };
    }

    // ---- Wait for the APNs token + backend save — 10s timeout ----
    let outcome;
    try {
      outcome = await rejectAfter(settlePromise, TOKEN_WAIT_TIMEOUT_MS, 'token+backend-save');
      lastCompletedOp = 'token-outcome-received';
    } catch (err) {
      const msg = err?.message || String(err);
      emit({ step: 'token-timeout', error: msg });
      emit({ step: 'registration-error', error: msg, stage: 'token-timeout' });
      _pendingRegistration = null;
      return { ok: false, stage: 'token-timeout', permission: perm.receive, error: msg };
    }

    if (outcome.ok) {
      return { ok: true, stage: 'success', permission: perm.receive, tokenLast8: outcome.tokenLast8 };
    }
    // Map internal stages from the listener to the caller-visible stage names.
    let mappedStage = 'registration-error';
    if (outcome.stage === 'token-save') mappedStage = 'token-save-error';
    return {
      ok: false,
      stage: mappedStage,
      permission: perm.receive,
      error: outcome.error,
      tokenLast8: outcome.tokenLast8,
    };
  })();

  // Race the real work against the watchdog. `try/finally` guarantees
  // the timer is cleared no matter which branch wins, so the caller's
  // "requesting" UI state is always released within 12s.
  try {
    return await Promise.race([inner, watchdogPromise]);
  } catch (err) {
    // The watchdog rejected. `inner` may still resolve later in the
    // background — we can't cancel a native bridge call — but the
    // caller has already been unblocked.
    const msg = err?.message || String(err);
    return { ok: false, stage: 'flow-watchdog-timeout', permission: null, error: msg, lastCompletedOp };
  } finally {
    if (watchdogTimer) clearTimeout(watchdogTimer);
  }
}

/**
 * Detach every listener and remove the token from the backend so this
 * device stops receiving pushes. Called on logout and on the toggle-off
 * path from the Profile page.
 */
export async function teardownIOSPushNotifications() {
  if (!isIOS()) return;

  // Best-effort backend cleanup — kill the row so send_ios_push stops
  // targeting this device. If the network fails we still detach the
  // listeners locally so the app doesn't re-register on the next screen.
  if (_lastTokenValue) {
    try {
      await axios.delete(`${API}/push/ios-token?token=${encodeURIComponent(_lastTokenValue)}`, {
        withCredentials: true,
        timeout: 8000,
      });
    } catch (_) { /* ignore */ }
    _lastTokenValue = null;
  }
  try {
    await withTimeout(PushNotifications.removeAllListeners(), NATIVE_CALL_TIMEOUT_MS, 'removeAllListeners');
  } catch (_) { /* ignore */ }
  _listenersAttached = false;
  _activeOnStep = null;
}

/**
 * Read the current OS permission WITHOUT triggering any prompts. Used
 * by the diagnostics "Refresh" button so the user can inspect state
 * on-demand. Wrapped in the same 5s timeout so it can never hang the UI.
 * Returns 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' |
 * 'unavailable' | 'error:<message>'.
 */
export async function readIOSPushPermission() {
  if (!(isNative() && isIOS())) return 'unavailable';
  try {
    const p = await withTimeout(PushNotifications.checkPermissions(), NATIVE_CALL_TIMEOUT_MS, 'checkPermissions');
    return p?.receive || 'unknown';
  } catch (err) {
    return `error:${err?.message || 'unknown'}`;
  }
}


/**
 * Read the app build info from three sources:
 *   1. iOS `App.getInfo()` — CFBundleShortVersionString / CFBundleVersion.
 *   2. Compile-time `REACT_APP_GIT_COMMIT_SHA` — the Git SHA baked
 *      into the JS bundle by the prebuild step. May be missing if the
 *      build environment had no `.git` and no CI env var.
 *   3. Runtime `/build-info.json` — the manifest emitted by
 *      `scripts/write-build-info.js` (postbuild). This ALWAYS carries
 *      `bundle_id` (the main.js asset hash) as a deterministic
 *      production identity, even when no real Git SHA is available,
 *      so the diagnostics panel and the stale-bundle guard both have
 *      something reliable to work with.
 *
 * Return fields (all callers must tolerate any subset being null):
 *   {
 *     version, build       — from App.getInfo()
 *     commitSha            — REAL Git SHA (7-char) if present, else null.
 *                            Never derived from the asset hash.
 *     bundleId             — main.js asset hash (production bundle id)
 *     productionSource     — 'main-js-hash' | '<env-key> env' |
 *                             'git rev-parse HEAD' | 'fallback'
 *     productionBuiltAt    — ISO timestamp string
 *     mainJsPath           — e.g. '/static/js/main.abc123.js'
 *   }
 */
export async function readBuildInfo() {
  const compiledCommitEnv = process.env.REACT_APP_GIT_COMMIT_SHA
    ? String(process.env.REACT_APP_GIT_COMMIT_SHA).trim()
    : '';
  const compiledIsReal = compiledCommitEnv && compiledCommitEnv !== 'unknown';

  // Fetch /build-info.json. Cache-bust with `?t=<epoch>` so a stale
  // CDN copy never masks a fresh deploy.
  let manifest = null;
  try {
    const res = await withTimeout(
      fetch(`/build-info.json?t=${Date.now()}`, { cache: 'no-store' }),
      NATIVE_CALL_TIMEOUT_MS,
      'fetch(build-info.json)',
    );
    if (res && res.ok) {
      manifest = await res.json();
    }
  } catch (_) { /* leave manifest null — UI degrades gracefully */ }

  const bundleId = manifest?.bundle_id || null;
  const productionSource = manifest?.source || null;
  const productionBuiltAt = manifest?.built_at || null;
  const mainJsPath = manifest?.main_js || null;

  // Real Git SHA identification: prefer manifest.commit when its
  // `source` is a git-ish source; fall back to the compile-time env
  // var when that is a real value. NEVER derived from the asset hash
  // (which has its own field).
  let commitSha = null;
  const manifestCommit = manifest?.commit;
  const manifestSourceIsGit = productionSource &&
    productionSource !== 'main-js-hash' &&
    productionSource !== 'fallback';
  if (manifestCommit && manifestCommit !== 'unknown' && manifestSourceIsGit) {
    commitSha = String(manifestCommit).slice(0, 7);
  } else if (compiledIsReal) {
    commitSha = compiledCommitEnv.slice(0, 7);
  }

  const base = {
    version: null,
    build: null,
    commitSha,
    bundleId,
    productionSource,
    productionBuiltAt,
    mainJsPath,
  };

  if (!(isNative() && isIOS())) {
    return base;
  }
  try {
    const mod = await import('@capacitor/app');
    const info = await withTimeout(mod.App.getInfo(), NATIVE_CALL_TIMEOUT_MS, 'App.getInfo');
    return {
      ...base,
      version: info?.version || null,
      build: info?.build || null,
    };
  } catch (err) {
    return { ...base, error: err?.message || String(err) };
  }
}
